import type { Locale } from "~/utils/locale";

/**
 * Sapo product types are Vietnamese warehouse labels ("HÀNG LẺ") that leak
 * raw onto English and Thai surfaces ("Shop More HÀNG LẺ", the /th category
 * filter — Thai partner feedback rows 2 and 10). Known values map per
 * locale; Thai surfaces use the English label, the same as the other
 * merchandise-line names in the Thai chrome ("YOGA & GYM", "PICKLEBALL").
 * Unknown values fall through unchanged.
 */
const PRODUCT_TYPE_LABELS: Record<string, { vi: string; en: string }> = {
  "HÀNG LẺ": { vi: "Hàng Lẻ", en: "Separates" },
  "HÀNG SET": { vi: "Hàng Set", en: "Sets" },
  SET: { vi: "Set", en: "Sets" },
  "PHỤ KIỆN": { vi: "Phụ Kiện", en: "Accessories" },
  "ÁO KHOÁC": { vi: "Áo Khoác", en: "Jackets" },
  "JACKET & COVER": { vi: "Jacket & Cover", en: "Jackets & Covers" },
  // Website audit 13 Oct 2026 (item 5): merchandise lines that are real
  // categories to a shopper get a tidy label in both languages.
  "YOGA & GYM": { vi: "Yoga & Gym", en: "Yoga & Gym" },
  PICKLEBALL: { vi: "Pickleball", en: "Pickleball" },
  TENNIS: { vi: "Tennis", en: "Tennis" },
  ACCESSORIES: { vi: "Phụ Kiện", en: "Accessories" },
  SETS: { vi: "Hàng Set", en: "Sets" },
  TOPS: { vi: "Áo", en: "Tops" },
  SKIRTS: { vi: "Váy", en: "Skirts" },
  SHORTS: { vi: "Quần Short", en: "Shorts" },
  LEGGINGS: { vi: "Legging", en: "Leggings" },
  "SPORTS BRAS": { vi: "Áo Bra", en: "Sports Bras" },
};

/**
 * Sapo productType values that are internal line codes or placeholders, not
 * categories a shopper would pick from ("-", "Focus", "Comfort", "Perform",
 * "Crush"). The legacy category facet skips them (audit 13 Oct 2026, item 5).
 */
const HIDDEN_PRODUCT_TYPES = new Set(["-", "—", "N/A", "NONE", "FOCUS", "COMFORT", "PERFORM", "CRUSH", "DEFAULT"]);

export function isShopperFacingProductType(productType: string | null | undefined): boolean {
  const v = (productType || "").trim();
  if (v.length < 2) return false;
  return !HIDDEN_PRODUCT_TYPES.has(v.toUpperCase());
}

export function displayProductType(productType: string | null | undefined, locale: Locale | string): string {
  if (!productType) return "";
  const mapped = PRODUCT_TYPE_LABELS[productType.trim().toUpperCase()];
  if (!mapped) return productType;
  return locale === "vi" ? mapped.vi : mapped.en;
}

/**
 * Raw productType spellings that mean the same category ("HÀNG SET", "SET",
 * "Sets" → Sets). The legacy category facet shows one entry per label and the
 * filter matches every spelling, case-insensitively (audit 13 Oct 2026, item 5).
 */
export function expandProductTypeAliases(values: string[]): string[] {
  const out = new Set<string>();
  for (const v of values) {
    out.add(v);
    const label = PRODUCT_TYPE_LABELS[v.trim().toUpperCase()]?.en;
    if (!label) continue;
    for (const [key, l] of Object.entries(PRODUCT_TYPE_LABELS)) if (l.en === label) out.add(key);
  }
  return [...out];
}
